import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Hero from "../components/Hero";
import Stats from "../components/Stats";
import Properties from "../components/Properties";
import RevealOnScroll from "../components/RevealOnScroll";
import { api } from "../lib/api";
import { logger } from "../lib/logger";
import { rememberScroll, restoreScroll } from "../lib/scrollMemory";

// Below-the-fold — code-split to keep the initial bundle lean.
// All these mount only after the user scrolls or after the page is idle.
const Services = lazy(() => import("../components/Services"));
const Agency = lazy(() => import("../components/Agency"));
const EstimationBanner = lazy(() => import("../components/EstimationBanner"));
const Testimonials = lazy(() => import("../components/Testimonials"));
const Contact = lazy(() => import("../components/Contact"));
const Footer = lazy(() => import("../components/Footer"));
const WhatsAppFab = lazy(() => import("../components/WhatsAppFab"));
const SupportFab = lazy(() => import("../components/SupportFab"));
const ExitIntentPopup = lazy(() => import("../components/ExitIntentPopup"));

// Skeleton fallback for lazy-loaded sections — keeps layout stable, no flash.
const SectionFallback = () => (
  <div aria-hidden="true" className="h-12 sm:h-16 md:h-20" />
);

export default function LandingPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [extrasReady, setExtrasReady] = useState(false);
  const { hash } = useLocation();

  useEffect(() => {
    let cancelled = false;
    api.stats()
      .then((data) => { if (!cancelled) setStats(data); })
      .catch((err) => { logger.warn("Stats fetch failed:", err?.message || err); });
    return () => { cancelled = true; };
  }, []);

  // Defer non-critical FABs (WhatsApp, Support, ExitIntent) until the browser is idle —
  // they don't need to be in the initial paint and shave ~25KB off LCP-blocking JS.
  // Hard timeout of 1500ms guarantees they appear quickly even on slow networks.
  useEffect(() => {
    const ric = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 800));
    const cic = window.cancelIdleCallback || window.clearTimeout;
    const handle = ric(() => setExtrasReady(true), { timeout: 1500 });
    return () => cic(handle);
  }, []);

  // Scroll to the requested anchor after mount/hash change
  useEffect(() => {
    if (!hash) return undefined;
    const id = hash.slice(1);
    const t = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(t);
  }, [hash]);

  // Restore saved scroll position when the visitor comes back from a property
  // detail page (e.g. browser back button or our "Retour" link). Runs ONCE on
  // mount. Does not interfere with hash-based anchors above — if a hash is
  // present, the anchor effect wins.
  useEffect(() => {
    if (hash) return;
    restoreScroll("/");
  }, [hash]);

  // Clicking a property card now goes straight to the full detail page —
  // no intermediate dialog/popup (per user request). We also remember the
  // current scroll so the visitor lands back exactly where they were.
  const openProperty = (p) => {
    rememberScroll("/");
    navigate(`/biens/${p.id}`);
  };

  return (
    <div className="bg-[#F9F8F6] text-[#1A1A1A]">
      <Navbar />
      <Hero />
      <RevealOnScroll><Stats stats={stats} /></RevealOnScroll>
      <RevealOnScroll><Properties onOpen={openProperty} /></RevealOnScroll>
      <Suspense fallback={<SectionFallback />}>
        <RevealOnScroll><Services /></RevealOnScroll>
        <RevealOnScroll><EstimationBanner /></RevealOnScroll>
        <RevealOnScroll><Agency /></RevealOnScroll>
        <RevealOnScroll><Testimonials /></RevealOnScroll>
        <RevealOnScroll><Contact /></RevealOnScroll>
        <Footer />
      </Suspense>
      {extrasReady && (
        <Suspense fallback={null}>
          <WhatsAppFab />
          <SupportFab />
          <ExitIntentPopup />
        </Suspense>
      )}
    </div>
  );
}
